<template>
  <v-container>
    <v-card class="mb-3">
      <v-toolbar
        flat
        dense
        :color="$vuetify.theme.dark ? 'blue-grey darken-4' : 'blue-grey lighten-5'"
      >
        <v-toolbar-title class="text-subtitle-2">
          Returned Products
        </v-toolbar-title>
        <v-spacer />
        <v-btn text :loading="loading" @click="fetchData">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="py-2">
        <v-row dense>
          <v-col cols="12" sm="4" md="3">
            <v-text-field
              v-model="dateFrom"
              label="Date from"
              type="date"
              hide-details="auto"
              clearable
              dense
              prepend-inner-icon="mdi-calendar-start"
            />
          </v-col>
          <v-col cols="12" sm="4" md="3">
            <v-text-field
              v-model="dateTo"
              label="Date to"
              type="date"
              hide-details="auto"
              clearable
              dense
              prepend-inner-icon="mdi-calendar-end"
            />
          </v-col>
          <v-col cols="12" sm="4" md="3">
            <v-text-field
              v-model="search"
              :loading="loading"
              append-icon="mdi-magnify"
              label="Search CSI / product"
              hide-details="auto"
              clearable
              dense
            />
          </v-col>
          <v-col cols="12" md="3" class="d-flex align-center">
            <div class="d-flex flex-wrap gap-8">
              <v-chip small label @click="presetRange('today')">
                Today
              </v-chip>
              <v-chip small label @click="presetRange('7d')">
                Last 7 days
              </v-chip>
              <v-chip small label @click="presetRange('30d')">
                Last 30 days
              </v-chip>
              <v-chip small label color="red" text-color="white" @click="presetRange('clear')">
                Clear
              </v-chip>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-data-table
      class="elevation-1"
      dense
      :headers="headers"
      :items="items"
      :items-per-page.sync="itemsPerPage"
      :page.sync="page"
      :server-items-length="total"
      :footer-props="{ 'items-per-page-options': [5, 10, 15, 20] }"
      :loading="loading"
    >
      <template v-slot:loading>
        <v-skeleton-loader type="table" class="mx-2 my-2" />
      </template>

      <template v-slot:item.reference_type="{ value }">
        <v-chip x-small label :color="value === 'SALE_INVENTORY' ? 'indigo' : 'teal'" dark>
          {{ value === 'SALE_INVENTORY' ? 'Sale' : 'Loan' }}
        </v-chip>
      </template>

      <template v-slot:item.good_order_quantity="{ value }">
        {{ value }}
      </template>

      <template v-slot:item.bad_order_quantity="{ value }">
        <span v-if="value > 0" class="red--text font-weight-medium">{{ value }}</span>
        <span v-else class="grey--text">—</span>
      </template>

      <template v-slot:item.retail="{ value }">
        {{ (value || 0).phpFormat() }}
      </template>

      <template v-slot:item.date_returned="{ value }">
        {{ (value || '').dateFormat() }}
      </template>

      <template v-slot:item.actions="{ item }">
        <v-btn text x-small color="yellow darken-3" @click="openEdit(item)">
          <v-icon small>
            mdi-pencil
          </v-icon>
        </v-btn>
        <v-btn text x-small color="red" @click="deleteItem(item)">
          <v-icon small>
            mdi-delete
          </v-icon>
        </v-btn>
      </template>

      <template v-slot:no-data>
        <div class="pa-6 text-center">
          <v-icon x-large class="mb-2">
            mdi-swap-horizontal
          </v-icon>
          <div class="mb-1 font-weight-medium">
            No returned products found
          </div>
          <div class="text-caption grey--text">
            Try adjusting dates or clearing the search
          </div>
        </div>
      </template>
    </v-data-table>

    <!-- Edit Dialog -->
    <v-dialog v-model="editDialog" max-width="420" persistent>
      <v-card>
        <v-card-title class="subtitle-1">
          Edit Returned Product
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model.number="editForm.goodOrderQuantity"
            label="Good Order Qty"
            type="number"
            min="0"
            outlined
            dense
            hide-details="auto"
            class="mt-2"
          />
          <v-text-field
            v-model.number="editForm.badOrderQuantity"
            label="Bad Order Qty"
            type="number"
            min="0"
            outlined
            dense
            hide-details="auto"
            class="mt-3"
          />
          <div class="mt-1 text-caption grey--text">
            Total: {{ (editForm.goodOrderQuantity || 0) + (editForm.badOrderQuantity || 0) }}
          </div>
          <v-text-field
            v-model="editForm.dateReturned"
            label="Date Returned"
            type="date"
            outlined
            dense
            hide-details="auto"
            class="mt-3"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="editDialog = false">
            Cancel
          </v-btn>
          <v-btn color="primary" :loading="saving" @click="saveEdit">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapState } from 'vuex'
import { authAxios } from '@/extra-functions/axios.extra'
import { ConfirmPopup, ErrorPopup, LoadingPopup, SuccessPopup, useDebounce } from '@/lib/utils'

const debounce = useDebounce()

export default {
  name: 'ReturnedProductsIndex',
  data() {
    return {
      headers: [
        { text: '', value: 'actions', sortable: false, width: 88 },
        { text: 'CSI No.', value: 'csi_number' },
        { text: 'Type', value: 'reference_type', sortable: false },
        { text: 'Product', value: 'returned_stock.product_name', sortable: false },
        { text: 'Good Qty', value: 'good_order_quantity', align: 'right', sortable: false },
        { text: 'Bad Qty', value: 'bad_order_quantity', align: 'right', sortable: false },
        { text: 'Unit', value: 'unit', sortable: false },
        { text: 'SRP', value: 'retail', align: 'right' },
        { text: 'Date Returned', value: 'date_returned' },
      ],
      items: [],
      total: 0,
      loading: false,
      itemsPerPage: 10,
      page: 1,
      search: null,
      dateFrom: null,
      dateTo: null,
      editDialog: false,
      saving: false,
      editTarget: null,
      editForm: {
        goodOrderQuantity: 1,
        badOrderQuantity: 0,
        dateReturned: null,
      },
    }
  },
  computed: {
    ...mapState({
      storeId: (state) => state.settings.storeId,
    }),
  },
  watch: {
    page() { this.fetchData() },
    itemsPerPage() { this.page = 1; this.fetchData() },
    search() { this.page = 1; debounce(this.fetchData) },
    dateFrom() { this.page = 1; this.fetchData() },
    dateTo() { this.page = 1; this.fetchData() },
  },
  created() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      try {
        this.loading = true
        const { data } = await authAxios().get('/v2/returned-products', {
          params: {
            limit: this.itemsPerPage,
            page: this.page,
            search: this.search || undefined,
            store_id: this.storeId || undefined,
            date_from: this.dateFrom || undefined,
            date_to: this.dateTo || undefined,
          },
        })
        this.items = data.data
        this.total = data.meta?.total ?? data.data.length
      } catch (e) {
        console.error(e)
        ErrorPopup.fire('Error', 'Failed to load returned products')
      } finally {
        this.loading = false
      }
    },

    openEdit(item) {
      this.editTarget = item
      this.editForm = {
        goodOrderQuantity: item.good_order_quantity ?? (item.quantity - (item.bad_order_quantity || 0)),
        badOrderQuantity: item.bad_order_quantity || 0,
        dateReturned: item.date_returned ? item.date_returned.slice(0, 10) : null,
      }
      this.editDialog = true
    },

    async saveEdit() {
      try {
        this.saving = true
        const item = this.editTarget
        const totalQty = (this.editForm.goodOrderQuantity || 0) + (this.editForm.badOrderQuantity || 0)
        const refKey = item.reference_type === 'SALE_INVENTORY'
          ? 'sale_inventory_id'
          : 'consumer_loan_inventory_id'
        await authAxios().put(`/v2/returned-products/${item.id}`, {
          data: {
            [refKey]: item.reference_id,
            quantity: totalQty,
            bad_order_quantity: this.editForm.badOrderQuantity || 0,
            date_returned: this.editForm.dateReturned
              ? `${this.editForm.dateReturned} 00:00:00`
              : null,
          },
        })
        this.editDialog = false
        SuccessPopup.fire('Success', 'Returned product updated')
        this.fetchData()
      } catch (e) {
        console.error(e)
        ErrorPopup.fire('Error', 'Failed to update returned product')
      } finally {
        this.saving = false
      }
    },

    async deleteItem(item) {
      const { isConfirmed } = await ConfirmPopup.fire(`Delete return for ${item.csi_number}?`)
      if (!isConfirmed) return
      try {
        LoadingPopup.fire('Deleting...')
        await authAxios().delete(`/v2/returned-products/${item.id}`)
        LoadingPopup.close()
        SuccessPopup.fire('Deleted', 'Returned product removed')
        this.fetchData()
      } catch (e) {
        LoadingPopup.close()
        console.error(e)
        ErrorPopup.fire('Error', 'Failed to delete returned product')
      }
    },

    presetRange(preset) {
      const fmt = (d) => {
        const y = d.getFullYear()
        const m = String(d.getMonth() + 1).padStart(2, '0')
        const da = String(d.getDate()).padStart(2, '0')
        return `${y}-${m}-${da}`
      }
      if (preset === 'clear') { this.dateFrom = null; this.dateTo = null; return }
      const today = new Date()
      if (preset === 'today') { const d = fmt(today); this.dateFrom = d; this.dateTo = d; return }
      const days = preset === '7d' ? 7 : 30
      const start = new Date(today)
      start.setDate(today.getDate() - (days - 1))
      this.dateFrom = fmt(start)
      this.dateTo = fmt(today)
    },
  },
}
</script>

<style scoped>
.gap-8 > * {
  margin-right: 0.5rem;
  margin-bottom: 0.25rem;
}
</style>
